import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../api";
import { PageHeader } from "../components/PageHeader";
import { StatCard } from "../components/StatCard";
import { StatusChip } from "../components/StatusChip";

interface Dashboard {
  summary: {
    activeClients: number;
    scans: number;
    images: number;
    completed: number;
    failed: number;
    successRate: number | null;
    openAlerts: number;
  };
  daily: any[];
  topClients: any[];
  recentErrors: any[];
  quotaRisks: any[];
}

export function DashboardPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<Dashboard>("/admin/dashboard")
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, []);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress />;
  const s = data.summary;
  return (
    <>
      <PageHeader
        title="Operations overview"
        subtitle="Live OCR volume, quality and client consumption."
      />
      <div className="metric-grid">
        <StatCard
          label="Active clients"
          value={s.activeClients}
          icon={<PeopleAltOutlinedIcon color="primary" />}
        />
        <StatCard
          label="Images - 30 days"
          value={s.images}
          caption={`${s.scans} logical scans`}
          icon={<ImageOutlinedIcon color="primary" />}
        />
        <StatCard
          label="Success rate"
          value={s.successRate == null ? "-" : `${s.successRate}%`}
          caption={`${s.completed} completed`}
          icon={<TaskAltOutlinedIcon color="success" />}
        />
        <StatCard
          label="Open alerts"
          value={s.openAlerts}
          caption={`${s.failed} failed scans`}
          icon={<WarningAmberOutlinedIcon color="warning" />}
        />
      </div>
      <Box className="page-grid" mt={2.5}>
        <Card sx={{ gridColumn: "span 8" }}>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Processing volume
            </Typography>
            <ResponsiveContainer width="100%" height={290}>
              <AreaChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="images"
                  stroke="#0b6b68"
                  fill="#bce7e2"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card sx={{ gridColumn: "span 4" }}>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Quota pressure
            </Typography>
            <Stack gap={2}>
              {data.quotaRisks.length ? (
                data.quotaRisks.map((item: any) => (
                  <Box key={item.clientId}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={700}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption">
                        {item.used}/{item.limitValue}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, item.percentage)}
                      color={
                        item.percentage >= 100
                          ? "error"
                          : item.percentage >= 90
                            ? "warning"
                            : "primary"
                      }
                      sx={{ height: 8, borderRadius: 8, mt: 0.7 }}
                    />
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">
                  No clients near their limits.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ gridColumn: "span 7" }}>
          <CardContent sx={{ p: 0 }}>
            <Box p={2.5}>
              <Typography variant="h6">Top clients</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Images</TableCell>
                  <TableCell align="right">Scans</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.topClients.map((r: any) => (
                  <TableRow key={r.clientId}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.images}</TableCell>
                    <TableCell align="right">{r.scans}</TableCell>
                    <TableCell align="right">{r.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card sx={{ gridColumn: "span 5" }}>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Recent failures
            </Typography>
            <Stack gap={1.5}>
              {data.recentErrors.slice(0, 6).map((r: any) => (
                <Box key={r.imageId}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography
                      fontWeight={700}
                      variant="body2"
                      className="truncate"
                    >
                      {r.clientName}
                    </Typography>
                    <StatusChip value="FAILED" />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="truncate"
                    display="block"
                  >
                    {r.errorMessage || "Unknown OCR failure"}
                  </Typography>
                </Box>
              ))}
              {!data.recentErrors.length && (
                <Typography color="text.secondary">
                  No recent errors.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
